import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Notice, Tag } from '../../../common/components/detail/detail';
import { Gate } from '../../../global/contexts/auth/auth';
import { BREAKDOWN_STATE_LABEL } from '../../tiers/utils/form';
import { useBreakdown } from '../contexts/breakdown';
import AgreeTierModal from './agree-tier';
import { readMissing, readReady } from '../utils/missing';
import Proposals from './proposals';

import '../../../common/components/detail/styles/detail.css';
import '../styles/breakdown.css';

// The breakdown of one tier: agree its scope, then see and authorize its task
// proposals.
//
// THE PRIMARY ACTION CHANGES WITH THE STATE. Drawing "Completar alcance",
// "Acordar tier", "Revisar propuestas" and "Autorizar selección" as four
// buttons of equal weight is not a screen that lets you choose — it is a screen
// that did not decide. Exactly one of them is the next thing to do.

const BREAKDOWN_TONE = { agreed: 'active', draft: 'warn', legacy: 'neutral' };

export default function BreakdownScreen({ tierId }) {
    const { data, isLoading, error } = useBreakdown(tierId);

    if (isLoading) return <p className="breakdown__loading">Cargando el desglose...</p>;

    if (error) {
        return (
            <p className="breakdown__failure">
                {error.status === 404
                    ? 'Este tier no está disponible: no existe, o queda fuera de tu alcance.'
                    : error.message || 'No se pudo cargar el desglose'}
            </p>
        );
    }

    const breakdown = data?.breakdown ?? data;
    const tier = breakdown?.tier ?? breakdown;

    return (
        <section className="breakdown">
            <Origin tier={tier} breakdown={breakdown} />
            <Agreement tierId={tierId} tier={tier} breakdown={breakdown} />
            <Proposals tierId={tierId} tier={tier} breakdown={breakdown} />
        </section>
    );
}

// WHERE THIS TIER CAME FROM, and it does not scroll away.
//
// A tier is built from an APPROVED VERSION of the project's plan, and the whole
// point of T01 is that the reference is exact and does not move. Somebody
// splitting work needs to see which plan and which version they are splitting
// without leaving the screen — otherwise they are breaking down what they
// remember.
function Origin({ tier, breakdown }) {
    const legacy = tier?.breakdown_state === 'legacy';
    const plan = breakdown?.plan ?? tier?.plan ?? null;
    const version = breakdown?.plan_version ?? tier?.plan_version ?? null;

    return (
        <aside className="breakdown__origin">
            <h3 className="breakdown__origin-title">De dónde sale este tier</h3>

            {legacy || (!plan && !version) ? (
                <p className="breakdown__origin-text">
                    <strong>Origen de planeación no registrado.</strong>{' '}
                    {legacy
                        ? 'Este tier es anterior a la planeación canónica: no salió de ninguna versión de plan, y no se le atribuye una.'
                        : 'Este tier no tiene una versión de plan enlazada.'}
                </p>
            ) : (
                <p className="breakdown__origin-text">
                    {plan?.name ?? 'Plan'}
                    {version?.number != null && <> · <strong>versión {version.number}</strong></>}
                    {version?.state && <> ({version.state})</>}
                    {plan?.id != null && (
                        <>
                            {' · '}
                            <Link to={`/admin/plans/${plan.id}${version?.number != null ? `/v/${version.number}` : ''}`}>
                                Leer el plan
                            </Link>
                        </>
                    )}
                </p>
            )}
        </aside>
    );
}

function Agreement({ tierId, tier, breakdown }) {
    const [confirming, setConfirming] = useState(false);

    const state = tier?.breakdown_state;
    const revision = tier?.breakdown_revision ?? breakdown?.breakdown_revision;
    const frozen = !!(breakdown?.frozen ?? tier?.frozen);
    const missing = readMissing(breakdown);
    const ready = readReady(breakdown);

    const hidden = breakdown?.hidden_dependencies ?? tier?.hidden_dependencies ?? 0;

    return (
        <section className="breakdown__card">
            <header className="breakdown__card-head">
                <h3 className="breakdown__card-title">Acuerdo del alcance</h3>
                <div className="breakdown__tags">
                    <Tag tone={BREAKDOWN_TONE[state] ?? 'neutral'}>
                        {BREAKDOWN_STATE_LABEL[state] ?? state}
                    </Tag>
                    {revision != null && <Tag>Revisión {revision}</Tag>}
                </div>
            </header>

            {state === 'legacy' && (
                <Notice>
                    Un tier <strong>legado</strong> no se acuerda: no tiene plan enlazado y nada
                    vuelve a este estado. Para autorizar propuestas nuevas bajo uno hay que
                    enlazarle un plan y acordarlo primero.
                </Notice>
            )}

            {frozen && (
                <Notice tone="warn">
                    El tier tiene <strong>tareas autorizadas</strong>: su contenido, sus
                    dependencias y su estado acordado quedaron congelados y no cambian por ninguna
                    puerta.
                </Notice>
            )}

            {state === 'agreed' && !frozen && (
                <Notice tone="accent">
                    El alcance está <strong>acordado en la revisión {revision}</strong>. Editar el
                    contenido del tier lo devuelve a borrador y hace falta acordarlo de nuevo.
                </Notice>
            )}

            {state === 'draft' && (
                <>
                    {missing === null ? (
                        <Notice>
                            El backend no informó qué falta para acordar este tier. Sin esa lista el
                            panel <strong>no la inventa</strong>: las condiciones incluyen el estado
                            de dependencias que esta pantalla puede no alcanzar a ver.
                        </Notice>
                    ) : missing.length === 0 ? (
                        <p className="breakdown__ok">
                            No falta nada: el tier se puede acordar.
                        </p>
                    ) : (
                        <div className="breakdown__missing">
                            <p className="breakdown__missing-title">
                                Falta esto para poder acordarlo:
                            </p>
                            <ul className="breakdown__missing-list">
                                {missing.map(item => (
                                    <li key={item.key}>{item.text}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Counted, never named. A dependency outside your scope
                        blocks the agreement just the same; showing its id would
                        be the leak the backend took care to avoid. */}
                    {hidden > 0 && (
                        <p className="breakdown__note">
                            Además, <strong>{hidden}</strong> dependencia{hidden === 1 ? '' : 's'}
                            {' '}queda{hidden === 1 ? '' : 'n'} fuera de tu alcance. Si alguna tiene
                            problemas, <strong>bloquea el acuerdo igual</strong> — se cuenta sin
                            nombrarse.
                        </p>
                    )}

                    <div className="breakdown__actions">
                        <Gate domain="tiers:write">
                            <Link to={`/admin/tiers/${tierId}/edit`} className="breakdown__button">
                                Completar el alcance
                            </Link>
                        </Gate>
                        <Gate domain="tiers:agree">
                            <button
                                type="button"
                                className="breakdown__button breakdown__button--primary"
                                onClick={() => setConfirming(true)}
                                disabled={ready === false}
                                title={ready === false ? 'Todavía falta algo de la lista de arriba' : undefined}
                            >
                                Acordar tier
                            </button>
                        </Gate>
                    </div>

                    {/* The same modal a tiers list row opens: one confirmation,
                        whichever door somebody came in by. */}
                    {confirming && (
                        <AgreeTierModal tierId={tierId} onClose={() => setConfirming(false)} />
                    )}
                </>
            )}
        </section>
    );
}
